/**
 * Price Level Labels Primitive
 *
 * Renders price level labels at the left edge of the chart.
 * Works with lightweight-charts price lines by drawing labels separately.
 */

import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';

// ============================================================================
// Types
// ============================================================================

export interface PriceLevelLabel {
  price: number;
  label: string;
  color: string;
}

// ============================================================================
// Renderer
// ============================================================================

class PriceLevelLabelsRenderer implements IPrimitivePaneRenderer {
  private _labels: Array<{ y: number; label: string; price: number; color: string }> = [];

  update(labels: Array<{ y: number; label: string; price: number; color: string }>): void {
    this._labels = labels;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const verticalPixelRatio = scope.verticalPixelRatio as number;

      const fontSize = Math.round(10 * pixelRatio);
      ctx.font = `800 ${fontSize}px sans-serif`;
      ctx.textBaseline = 'bottom';
      const labelX = 5 * pixelRatio;

      for (const { y, label, price, color } of this._labels) {
        const text = `${label} ${price.toFixed(2)}`;
        // Position label above the line with generous offset
        const labelY = (y - 6) * verticalPixelRatio;

        // Dark background pill for contrast
        const textWidth = ctx.measureText(text).width;
        const padding = 3 * pixelRatio;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
        ctx.fillRect(labelX - padding, labelY - fontSize - padding, textWidth + padding * 2, fontSize + padding * 2);

        // Draw text
        ctx.fillStyle = color;
        ctx.fillText(text, labelX, labelY);
      }
    });
  }
}

// ============================================================================
// Pane View
// ============================================================================

class PriceLevelLabelsPaneView implements IPrimitivePaneView {
  private _source: PriceLevelLabelsPrimitive;
  private _renderer: PriceLevelLabelsRenderer;

  constructor(source: PriceLevelLabelsPrimitive) {
    this._source = source;
    this._renderer = new PriceLevelLabelsRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top'; // Draw on top of everything
  }

  renderer(): IPrimitivePaneRenderer {
    const labels = this._source.getRendererData();
    this._renderer.update(labels);
    return this._renderer;
  }
}

// ============================================================================
// Primitive
// ============================================================================

export class PriceLevelLabelsPrimitive implements ISeriesPrimitive<Time> {
  // Labels stored by source key, allowing multiple sources to contribute labels
  private _labelsBySource: Map<string, PriceLevelLabel[]> = new Map();
  private _paneView: PriceLevelLabelsPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];

  constructor() {
    this._paneView = new PriceLevelLabelsPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  /**
   * Set labels from a specific source (merges with other sources)
   * @param source - Unique identifier for the label source (e.g., 'sessionHodLod', 'priceLevels')
   * @param labels - Labels from this source
   */
  setLabels(source: string, labels: PriceLevelLabel[]): void {
    this._labelsBySource.set(source, labels);
    this._requestUpdate?.();
  }

  /**
   * Update all labels (replaces all sources with single 'default' source)
   * @deprecated Use setLabels() for better multi-source support
   */
  updateLabels(labels: PriceLevelLabel[]): void {
    this._labelsBySource.clear();
    this._labelsBySource.set('default', labels);
    this._requestUpdate?.();
  }

  /**
   * Clear labels from a specific source
   */
  clearSource(source: string): void {
    this._labelsBySource.delete(source);
    this._requestUpdate?.();
  }

  /**
   * Clear all labels
   */
  clearLabels(): void {
    this._labelsBySource.clear();
    this._requestUpdate?.();
  }

  /**
   * Get all labels merged from all sources
   */
  private _getAllLabels(): PriceLevelLabel[] {
    const allLabels: PriceLevelLabel[] = [];
    for (const labels of this._labelsBySource.values()) {
      allLabels.push(...labels);
    }
    return allLabels;
  }

  /**
   * Get renderer data with calculated Y coordinates
   */
  getRendererData(): Array<{ y: number; label: string; price: number; color: string }> {
    const labels = this._getAllLabels();
    if (!this._series || labels.length === 0) {
      return [];
    }

    // Convert to Y coordinates
    const withY: Array<{ y: number; label: string; price: number; color: string }> = [];
    for (const { price, label, color } of labels) {
      const y = this._series.priceToCoordinate(price);
      if (y !== null) {
        withY.push({ y, label, price, color });
      }
    }

    // Sort by Y position for grouping
    withY.sort((a, b) => a.y - b.y);

    // Merge labels that are within 12px of each other (same or very close prices)
    const MERGE_THRESHOLD_PX = 12;
    const merged: Array<{ y: number; label: string; price: number; color: string }> = [];

    for (const item of withY) {
      const last = merged[merged.length - 1];
      if (last && Math.abs(item.y - last.y) < MERGE_THRESHOLD_PX) {
        // Merge: combine labels with "/" separator
        last.label = `${last.label}/${item.label}`;
        // Keep the average Y position
        last.y = (last.y + item.y) / 2;
      } else {
        merged.push({ ...item });
      }
    }

    return merged;
  }
}

// ============================================================================
// Factory Function
// ============================================================================

export function createPriceLevelLabelsPrimitive(): PriceLevelLabelsPrimitive {
  return new PriceLevelLabelsPrimitive();
}
